import { apiFetch } from "@/lib/api";

export type ReceiptDiscrepancyLine = {
  sku: string;
  quantity: number;
  unit_of_measure: string | null;
  receipt_item_id: string | null;
  unit_price: number | null;
  line_total: number | null;
  expected_quantity: number | null;
  is_fee: boolean;
  differs: boolean;
  manufacturer_part: string | null;
  part_differs: boolean;
};

export type ReceiptDiscrepancyVendorInvoice = {
  id: string;
  vendor_invoice_number: string | null;
  invoice_date: string | null;
  currency: string | null;
  receipt_match_status: string | null;
  lines: ReceiptDiscrepancyLine[];
};

export type ReceiptDiscrepancyQuantityOverride = {
  reason: string;
  overridden_at: string;
  overridden_by_name: string | null;
};

export type ReceiptDiscrepancyReceipt = {
  id: string;
  status: string;
  received_at: string | null;
  lines: ReceiptDiscrepancyLine[];
  quantity_override: ReceiptDiscrepancyQuantityOverride | null;
};

export type ReceiptDiscrepancyWorkOrder = {
  build_order_id: string;
  work_order_number: string | null;
  sku: string;
  quantity: number;
  unit_of_measure: string | null;
  labour_cost: number | null;
  differs: boolean;
};

export type ReceiptDiscrepancyDifference = {
  sku: string;
  unit_of_measure: string | null;
  ordered_quantity: number | null;
  vendor_invoice_quantity: number;
  receipt_quantity: number;
  work_order_quantity: number | null;
};

export type ReceiptDiscrepancyHoldReason = {
  sku: string;
  reasons: string[];
};

export type ReceiptDiscrepancyPurchaseOrder = {
  purchase_order_id: string;
  purchase_order_number: string | null;
  supplier_name: string | null;
  has_work_orders: boolean;
  invoice_exceeds_purchase_order: boolean;
  differences: ReceiptDiscrepancyDifference[];
  hold_reasons: ReceiptDiscrepancyHoldReason[];
  vendor_invoices: ReceiptDiscrepancyVendorInvoice[];
  receipts: ReceiptDiscrepancyReceipt[];
  work_orders: ReceiptDiscrepancyWorkOrder[];
  cm_finished_goods?: {
    wofg_posted_quantity: number;
    transfer_count: number;
    stays_at_build_site: boolean;
  } | null;
};

const RECEIPT_MATCH_LABEL: Record<string, string> = {
  awaiting_receipt: "Awaiting receipt",
  matched: "Receipt matched",
  mismatch: "Receipt discrepancy",
  matched_with_override: "Receipt matched (override)",
  overage_accepted_free: "Overage accepted as free",
  excess_allowed: "Excess material allowed",
  excess_payment_refused: "Excess payment refused",
};

const QUANTITY_EPSILON = 1e-6;

const RECEIPT_STATUS_LABEL: Record<string, string> = {
  draft: "Draft",
  submitted: "Submitted",
  posted_to_mv: "Posted",
  voided: "Voided",
};

export function receiptMatchStatusLabel(status: string | null): string {
  if (status == null) return "";
  return RECEIPT_MATCH_LABEL[status] ?? status;
}

export function receiptStatusLabel(status: string): string {
  return RECEIPT_STATUS_LABEL[status] ?? status;
}

export function formatReceiptDiscrepancyQuantity(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(value);
}

export function formatReceiptDiscrepancyMoney(value: number | null, currency: string | null): string {
  if (value == null || !Number.isFinite(value)) return "";
  const code = currency != null && /^[A-Z]{3}$/.test(currency) ? currency : "USD";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: code }).format(value);
}

export function formatReceiptDiscrepancyDate(value: string | null): string {
  if (value == null || value.trim() === "") return "";
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (dateOnly != null) {
    const date = new Date(Date.UTC(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3])));
    return date.toLocaleDateString("en-US", { timeZone: "UTC" });
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function receiptDiscrepancyPurchaseOrderMatches(
  purchaseOrder: ReceiptDiscrepancyPurchaseOrder,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  const haystack = [
    purchaseOrder.purchase_order_number,
    purchaseOrder.purchase_order_id,
    purchaseOrder.supplier_name,
    ...purchaseOrder.differences.map((row) => row.sku),
    ...purchaseOrder.vendor_invoices.flatMap((invoice) => [
      invoice.vendor_invoice_number,
      ...invoice.lines.map((line) => line.sku),
    ]),
    ...purchaseOrder.receipts.flatMap((receipt) => receipt.lines.map((line) => line.sku)),
    ...purchaseOrder.work_orders.flatMap((workOrder) => [workOrder.work_order_number, workOrder.sku]),
  ];
  return haystack.some((value) => (value ?? "").toLowerCase().includes(needle));
}

/** True when a submitted receipt is above a positive vendor-invoice quantity. */
export function vendorInvoiceCanAcceptOverage(
  receiptMatchStatus: string | null,
  differences: ReceiptDiscrepancyDifference[],
): boolean {
  if (receiptMatchStatus !== "mismatch") return false;
  return differences.some(
    (difference) =>
      difference.vendor_invoice_quantity > QUANTITY_EPSILON
      && difference.receipt_quantity > difference.vendor_invoice_quantity + QUANTITY_EPSILON,
  );
}

export async function overrideReceiptQuantities(input: {
  receiptId: string;
  reason: string;
  reprint: boolean;
  printer: string;
  lines: Array<{ inbound_receipt_item_id: string; quantity: number }>;
}): Promise<{ print_error: string | null; reprinted_subcontainers: number[] }> {
  return apiFetch<{ print_error: string | null; reprinted_subcontainers: number[] }>(
    `/api/accounting/ap/receipts/${encodeURIComponent(input.receiptId)}/quantity-override`,
    {
      method: "POST",
      body: {
        reason: input.reason,
        reprint: input.reprint,
        printer: input.printer,
        lines: input.lines,
      },
    },
  );
}

export async function approveReceiptOverageAsFree(invoiceId: string): Promise<void> {
  await apiFetch(`/api/accounting/ap/invoices/${encodeURIComponent(invoiceId)}/approve-overage-free`, {
    method: "POST",
  });
}

export async function allowReceiptDiscrepancyExcessMaterial(
  purchaseOrderId: string,
): Promise<{ updated_invoices: number; gipi_created: number; gipi_error: string | null }> {
  return apiFetch(
    `/api/accounting/ap/receipt-discrepancy/${encodeURIComponent(purchaseOrderId)}/allow-excess-material`,
    { method: "POST" },
  );
}

export async function refuseReceiptDiscrepancyExcessPayment(
  purchaseOrderId: string,
): Promise<{ updated_invoices: number; gipi_created: number; gipi_error: string | null }> {
  return apiFetch(
    `/api/accounting/ap/receipt-discrepancy/${encodeURIComponent(purchaseOrderId)}/refuse-excess-payment`,
    { method: "POST" },
  );
}

export async function fetchReceiptDiscrepancyPurchaseOrders(): Promise<ReceiptDiscrepancyPurchaseOrder[]> {
  const data = await apiFetch<{ purchase_orders: ReceiptDiscrepancyPurchaseOrder[] }>(
    "/api/accounting/ap/receipt-discrepancy",
  );
  return data.purchase_orders;
}
